import { z } from "zod";

/**
 * Adapts a zod schema into a mongoose field validator, so save-time validation can
 * share the exact rules already declared for zod/DTO validation instead of
 * hand-rolling them.
 *
 * `null`/`undefined` values pass (use mongoose `required` for presence). Zod issues
 * are reported as `<path>: <message>` joined with "; " — mongoose prefixes its own
 * full field path when surfacing the error, so no field label is needed.
 */
export function mongooseValidatorFromZod(schema: z.ZodType) {
    return {
        validator: function (this: unknown, value: unknown): boolean {
            if (value === null || value === undefined) return true;
            const parsed = schema.safeParse(value);
            if (parsed.success) return true;
            throw new Error(
                parsed.error.issues
                    .map((issue) => {
                        let where = "";
                        issue.path.forEach((p) => {
                            where += typeof p === "number" ? `[${p}]` : where ? `.${String(p)}` : String(p);
                        });
                        return where ? `${where}: ${issue.message}` : issue.message;
                    })
                    .join("; "),
            );
        },
        message: (props: { reason?: { message?: string } }) => props.reason?.message ?? "value is invalid.",
    };
}
